"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ShieldCheck, ChevronRight, FileText, CircleCheck as CheckCircle2, Activity, Sparkles, Zap, Flame } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const applications = [{
  label: "First Responders",
  icon: Flame
}, {
  label: "Border Security",
  icon: ShieldCheck
}, {
  label: "Law Enforcement",
  icon: Activity
}, {
  label: "CBRNe Defense",
  icon: Zap
}];
const features = ["Substance ID through containers & packaging", "Mil-Spec ruggedized drop-tested chassis", "Instant narcotics, precursor & explosive libraries"];
export default function HeroThreat() {
  const shouldReduceMotion = useReducedMotion();
  const transitionConfig = shouldReduceMotion ? {
    duration: 0
  } : {
    duration: 0.5,
    ease: "easeOut"
  };
  return <section id="herothreatidentification" data-nav="light" className="relative bg-background text-foreground overflow-hidden pt-12 pb-20 md:pt-16 md:pb-28 lg:pt-20 lg:pb-32 border-b border-border">
      {/* Subtle grid accent background */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,var(--color-secondary),transparent_50%)] pointer-events-none opacity-70" />
      <div className="absolute inset-y-0 right-0 w-1/3 bg-radial from-accent/30 to-transparent pointer-events-none" />

      <div className="page-container relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {/* Left Column (7 Columns) */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={transitionConfig} className="lg:col-span-7 flex flex-col items-start">
            {/* Eyebrow Badge */}
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary border border-border text-secondary-foreground mb-6">
              <Sparkles className="size-4 text-primary" />
              <span className="text-xs font-semibold uppercase tracking-wider">
                Portable Spectroscopy & Threat Identification
              </span>
            </div>

            {/* Main H1 Heading */}
            <h1 className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-5xl font-bold tracking-tight text-foreground leading-[1.12] mb-6">
              Field-Ready Chemical &amp; Threat Identification
            </h1>

            {/* Factual Lead Paragraph */}
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mb-8">
              Rugged handheld Raman and NIR spectrometers built for rapid, non-destructive identification of unknown chemicals, illicit narcotics, hazardous precursors, and explosives. Engineered for first responders, border security teams, law enforcement agencies, and CBRNe defense units operating in critical field environments.
            </p>

            {/* Key Capabilities Checklist */}
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8 w-full max-w-2xl">
              {features.map((feature, i) => <li key={i} data-index={i} className="flex items-center gap-2.5 text-sm font-medium text-foreground">
                  <CheckCircle2 className="size-4 text-primary shrink-0" />
                  <span>{feature}</span>
                </li>)}
            </ul>

            {/* Dual CTA Buttons */}
            <div className="flex flex-wrap items-center gap-4 w-full sm:w-auto mb-10">
              <Button asChild size="lg" className="w-full sm:w-auto shadow-sm">
                <Link to="/BookADemoPage">
                  Book a Field Demonstration
                  <ChevronRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
                <Link to="/RamanHandheldAnalyzersPage">
                  <FileText className="size-4" />
                  Explore Handheld Analyzers
                </Link>
              </Button>
            </div>

            {/* Target Sectors Quick Tags */}
            <div className="pt-6 border-t border-border w-full">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground mb-3">
                Operational Sectors Served
              </p>
              <div className="flex flex-wrap gap-2">
                {applications.map((app, i) => {
                const IconComponent = app.icon;
                return <div key={app.label} data-index={i} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-secondary text-secondary-foreground text-xs font-medium border border-border">
                      <IconComponent className="size-3.5 text-primary" />
                      <span>{app.label}</span>
                    </div>;
              })}
              </div>
            </div>
          </motion.div>

          {/* Right Column - Flagship Device Render (5 Columns) */}
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          ...transitionConfig,
          delay: shouldReduceMotion ? 0 : 0.15
        }} className="lg:col-span-5 relative">
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {/* Decorative device backdrop glow */}
              <div className="absolute -inset-4 rounded-2xl bg-secondary/80 blur-xl -z-10" />

              {/* Main Card Wrapper */}
              <div className="relative rounded-xl border border-border bg-card p-4 sm:p-6 shadow-md overflow-hidden">
                {/* Status bar atop card */}
                <div className="flex items-center justify-between pb-4 mb-4 border-b border-border">
                  <div className="flex items-center gap-2">
                    <span className="size-2.5 rounded-full bg-emerald-500 animate-pulse" />
                    <span className="text-xs font-semibold tracking-wide text-foreground uppercase">
                      Direct Spectral Matching Active
                    </span>
                  </div>
                  <Badge variant="outline" className="text-[11px] font-mono">
                    MIL-STD-810H
                  </Badge>
                </div>

                {/* Flagship Device Image */}
                <div className="relative rounded-lg overflow-hidden bg-muted aspect-4/3 flex items-center justify-center border border-border">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/handheld_threat_spectrometer_render.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/handheld_threat_spectrometer_render.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/handheld_threat_spectrometer_render.png-wvc-sizes" alt="Handheld Raman chemical threat identification spectrometer device" loading="lazy" decoding="async" className="w-full h-full object-cover object-center" />
                  
                  {/* Floating Telemetry Badge */}
                  <div className="absolute bottom-3 left-3 right-3 bg-card/95 backdrop-blur-xs border border-border rounded-md p-2.5 shadow-sm flex items-center justify-between gap-2">
                    <div>
                      <p className="text-[11px] text-muted-foreground">Scan Time</p>
                      <p className="text-xs font-bold text-foreground">Performance depends on the instrument, application, and sample matrix</p>
                    </div>
                    <div className="h-6 w-px bg-border" />
                    <div>
                      <p className="text-[11px] text-muted-foreground">Library</p>
                      <p className="text-xs font-bold text-foreground">Extensive reference library</p>
                    </div>
                    <div className="h-6 w-px bg-border" />
                    <div>
                      <p className="text-[11px] text-muted-foreground">Protection</p>
                      <p className="text-xs font-bold text-primary font-mono">IP67 Rated</p>
                    </div>
                  </div>
                </div>

                {/* Model Tagline and Consultation link */}
                <div className="mt-4 flex items-center justify-between">
                  <div>
                    <h2 className="text-sm font-bold text-card-foreground">
                      Antech Handheld Series
                    </h2>
                    <p className="text-xs text-muted-foreground">
                      Point-and-shoot laser spectroscopy
                    </p>
                  </div>
                  <Button asChild variant="ghost" size="sm" className="text-xs font-semibold">
                    <Link to="/DiscussYourApplicationPage">
                      Application Inquiry
                      <ChevronRight className="size-3" />
                    </Link>
                  </Button>
                </div>
              </div>

              {/* Floating Certification Badge */}
              <div className="hidden sm:flex absolute -bottom-5 -left-5 bg-card border border-border rounded-lg p-3 shadow-md items-center gap-3">
                <div className="size-8 rounded-md bg-primary/10 flex items-center justify-center text-primary">
                  <ShieldCheck className="size-5" />
                </div>
                <div>
                  <p className="text-xs font-bold text-foreground">Immediate Decision Support</p>
                  <p className="text-[11px] text-muted-foreground">Color-coded hazard triage output</p>
                </div>
              </div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>;
}